import {FirebaseAnalyticsTypes} from '@react-native-firebase/analytics';

import {SomeRequired} from '../typings';

// References:
// - LMG GA4 Events: https://docs.google.com/spreadsheets/d/1C9er7TwpT7B2OMfBxxSerTyniD0VVFOjZ7khFpXloi4/edit#gid=0
// - [GA4] Recommended events
//   - https://support.google.com/analytics/answer/9267735?hl=en
//   - https://support.google.com/firebase/answer/9267735?hl=en (same as above)
export enum EventName {
  // Recommended - For all properties
  Login = 'login',
  SignUp = 'sign_up',
  SelectContent = 'select_content',

  // Recommended - For online sales
  AddToCart = 'add_to_cart',
  Purchase = 'purchase',

  // Custom
  DeleteAccount = 'delete_account',
  ForgotPassword = 'forgot_password',
  Checkout = 'checkout',
  SubmitOrder = 'submit_order',
  ViewActivation = 'view_activation',
  Navigation = 'navigation',
  LocationSelected = 'location_selected',
  StoreSelected = 'stored_selected',
  Logout = 'logout',
  IncreaseCartItemQty = 'increase_cart_item_quantity',
  DecreaseCartItemQty = 'decrease_cart_item_quantity',
  PasswordResetRequest = 'password_reset_request',
}

export enum EventScreenName {
  Welcome = 'welcome',
  FindSuburb = 'find_suburb',
  ChooseStore = 'choose_store',
  Today = 'today',
  Shop = 'shop',
  Discover = 'discover',
  Wishlist = 'wishlist',
  Catalogues = 'catalogues',
  Promotions = 'promotions',
  Cart = 'cart',
  Checkout = 'checkout',
  Account = 'account',
  Information = 'information',
  ResponsibleService = 'responsible_service',
  SelfExclusion = 'self_exclusion',
  Login = 'login',
  SuburbSelection = 'suburb_selection',
  StoreSelection = 'store_selection',
  AccountEntry = 'account_entry',
}

export enum EventSourceType {
  System = 'system',
  Button = 'button',
  Link = 'link',
}

export type AnalyticsEvent =
  | LoginEvent
  | SignUpEvent
  | DeleteAccountEvent
  | ForgotPasswordEvent
  | AddToCartEvent
  | CheckoutEvent
  | SubmitOrderEvent
  | PurchaseEvent
  | SelectContentEvent
  | ViewActivationEvent
  | NavigationEvent
  | LocationSelectedEvent
  | StoreSelectedEvent
  | LogoutEvent
  | IncreaseCartItemQtyEvent
  | DecreaseCartItemQtyEvent
  | PasswordResetRequestEvent;

type CommonAnalyticsItemParameters = SomeRequired<
  FirebaseAnalyticsTypes.Item,
  'item_id' | 'item_name' | 'price' | 'quantity' | 'item_brand'
> & {
  discount: number;
};

export type EventCommon = {
  eventName: EventName;
  parameters: EventParamCommon;
};

type EventParamCommon = {
  screen_name?: string;
  source_type?: string;
  traffic_type?: string;
} & {
  [key: string]: any;
};

// Recommended - For all properties

type LoginEvent = EventCommon & {
  eventName: EventName.Login;
  parameters: FirebaseAnalyticsTypes.LoginEventParameters;
};

type SignUpEvent = EventCommon & {
  eventName: EventName.SignUp;
  parameters: FirebaseAnalyticsTypes.SignUpEventParameters;
};

type SelectContentEvent = EventCommon & {
  eventName: EventName.SelectContent;
  parameters: Record<string, unknown>;
};

// Recommended - For online sales

export type AddToCartEvent = EventCommon & {
  eventName: EventName.AddToCart;
  parameters: SomeRequired<
    FirebaseAnalyticsTypes.AddToCartEventParameters,
    'value' | 'items' | 'currency'
  > & {
    items: CommonAnalyticsItemParameters[];
  };
};

export type PurchaseEvent = EventCommon & {
  eventName: EventName.Purchase;
  parameters: SomeRequired<
    FirebaseAnalyticsTypes.PurchaseEventParameters,
    'transaction_id' | 'value' | 'tax' | 'items' | 'currency'
  > & {
    order_number: string;
    items: CommonAnalyticsItemParameters[];
  };
};

// Custom

type DeleteAccountEvent = EventCommon & {
  eventName: EventName.DeleteAccount;
  parameters: Record<string, unknown>;
};

type ForgotPasswordEvent = EventCommon & {
  eventName: EventName.ForgotPassword;
  parameters: Record<string, unknown>;
};

type CheckoutEvent = EventCommon & {
  eventName: EventName.Checkout;
  parameters: SomeRequired<
    FirebaseAnalyticsTypes.PurchaseEventParameters,
    'value' | 'tax' | 'items' | 'currency'
  > & {
    // items: CommonAnalyticsItemParameters[],
  };
};

export type SubmitOrderEvent = CheckoutEvent;

type ViewActivationEvent = EventCommon & {
  eventName: EventName.ViewActivation;
  parameters: {
    promotion_name: string;
    competition_id: string;
    order_number: string;
  };
};

type NavigationEvent = EventCommon & {
  eventName: EventName.Navigation;
  parameters: Record<string, unknown>;
};

type LocationSelectedEvent = EventCommon & {
  eventName: EventName.LocationSelected;
  parameters: Record<string, unknown>;
};

type StoreSelectedEvent = EventCommon & {
  eventName: EventName.StoreSelected;
  parameters: Record<string, unknown>;
};

type LogoutEvent = EventCommon & {
  eventName: EventName.Logout;
  parameters: Record<string, unknown>;
};

type IncreaseCartItemQtyEvent = EventCommon & {
  eventName: EventName.IncreaseCartItemQty;
  parameters: Record<string, unknown>;
};

type DecreaseCartItemQtyEvent = EventCommon & {
  eventName: EventName.DecreaseCartItemQty;
  parameters: Record<string, unknown>;
};

type PasswordResetRequestEvent = EventCommon & {
  eventName: EventName.PasswordResetRequest;
  parameters: Record<string, unknown>;
};
